import { useState, type FormEvent } from 'react';

type Lang = 'pt' | 'en';
type Objective = 'more-leads' | 'better-follow-up' | 'more-bookings';

interface ContactSectionProps {
  lang: Lang;
}

interface ContactFormData {
  name: string;
  email: string;
  website: string;
  objective: Objective;
  message: string;
  consent: boolean;
  honeypot: string;
}

interface ContactFormErrors {
  name?: string;
  email?: string;
  website?: string;
  objective?: string;
  message?: string;
  consent?: string;
}

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export function ContactSection({ lang }: ContactSectionProps) {
  const t = {
    pt: {
      label: 'COMEÇA AQUI',
      headline1: 'Estás a perder leads',
      headline2: 'sem saber?',
      description: 'Tens 30 minutos?\nDescobre onde estás a perder clientes e o que automatizar primeiro.\nSem compromisso.',
      submitIdle: 'Enviar pedido',
      submitLoading: 'A enviar...',
      submitSuccess: 'Pedido enviado',
      fallback: 'Preferes marcar diretamente? Abre o Calendly.',
      trust1: 'Baseados em Portugal, disponíveis remotamente',
      trust2: 'Respondemos em menos de 24 horas',
      fields: {
        nameLabel: 'Nome',
        namePlaceholder: 'O teu nome',
        emailLabel: 'Email',
        emailPlaceholder: 'nome@empresa.com',
        websiteLabel: 'Website',
        websitePlaceholder: 'https://teusite.com',
        objectiveLabel: 'Objetivo principal',
        objectiveOptions: {
          moreLeads: 'Gerar mais leads',
          betterFollowUp: 'Melhorar o follow-up',
          moreBookings: 'Aumentar marcações',
        },
        messageLabel: 'Mensagem',
        messagePlaceholder: 'Conta-nos sobre o teu negócio e o que queres melhorar.',
        consentLabel: 'Concordo em ser contactado pela SimplesFlow sobre este pedido.',
      },
      validation: {
        nameRequired: 'Indica o teu nome.',
        emailRequired: 'Indica o teu email.',
        emailInvalid: 'Indica um email válido.',
        websiteRequired: 'Indica o teu website.',
        objectiveRequired: 'Seleciona o teu objetivo.',
        messageRequired: 'Escreve uma mensagem.',
        consentRequired: 'Precisas de aceitar o consentimento.',
      },
      status: {
        success: 'Obrigado! Recebemos o teu pedido e vamos responder em breve.',
        fixErrors: 'Revê os campos assinalados e tenta novamente.',
        webhookMissing: 'Formulário indisponível de momento. Tenta novamente mais tarde.',
        webhookInactive: 'Webhook inativo no Make. Ativa o cenário e tenta novamente.',
        errorGeneric: 'Não foi possível enviar agora. Tenta novamente dentro de minutos.',
      },
    },
    en: {
      label: 'GET STARTED',
      headline1: 'Are you losing leads',
      headline2: 'without knowing?',
      description: 'Got 30 minutes?\nDiscover where you\'re losing clients and what to automate first.\nNo commitment.',
      submitIdle: 'Send request',
      submitLoading: 'Sending...',
      submitSuccess: 'Request sent',
      fallback: 'Prefer direct booking? Open Calendly.',
      trust1: 'Based in Portugal, available remotely',
      trust2: 'We respond within 24 hours',
      fields: {
        nameLabel: 'Name',
        namePlaceholder: 'Your name',
        emailLabel: 'Email',
        emailPlaceholder: 'name@company.com',
        websiteLabel: 'Website',
        websitePlaceholder: 'https://your-site.com',
        objectiveLabel: 'Primary objective',
        objectiveOptions: {
          moreLeads: 'Get more leads',
          betterFollowUp: 'Improve follow-up',
          moreBookings: 'Increase bookings',
        },
        messageLabel: 'Message',
        messagePlaceholder: 'Tell us about your business and what you want to improve.',
        consentLabel: 'I agree to be contacted by SimplesFlow about this request.',
      },
      validation: {
        nameRequired: 'Please enter your name.',
        emailRequired: 'Please enter your email.',
        emailInvalid: 'Please enter a valid email.',
        websiteRequired: 'Please enter your website.',
        objectiveRequired: 'Please select your objective.',
        messageRequired: 'Please add a message.',
        consentRequired: 'You need to accept consent.',
      },
      status: {
        success: 'Thanks! We received your request and will reply soon.',
        fixErrors: 'Please review the highlighted fields and try again.',
        webhookMissing: 'Form is temporarily unavailable. Please try again later.',
        webhookInactive: 'Make webhook is inactive. Turn on the scenario and try again.',
        errorGeneric: 'Could not send right now. Please retry in a few minutes.',
      },
    },
  };

  const text = t[lang];
  const webhookUrl = import.meta.env.VITE_MAKE_WEBHOOK_URL?.trim();
  const timeoutMs = Number(import.meta.env.VITE_CONTACT_TIMEOUT_MS ?? 10000);

  const [form, setForm] = useState<ContactFormData>({
    name: '',
    email: '',
    website: '',
    objective: 'more-leads',
    message: '',
    consent: false,
    honeypot: '',
  });
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const updateField = <K extends keyof ContactFormData>(key: K, value: ContactFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): ContactFormErrors => {
    const next: ContactFormErrors = {};
    if (!form.name.trim()) next.name = text.validation.nameRequired;
    if (!form.email.trim()) {
      next.email = text.validation.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = text.validation.emailInvalid;
    }
    if (!form.website.trim()) next.website = text.validation.websiteRequired;
    if (!form.objective) next.objective = text.validation.objectiveRequired;
    if (!form.message.trim()) next.message = text.validation.messageRequired;
    if (!form.consent) next.consent = text.validation.consentRequired;
    return next;
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (form.honeypot.trim()) {
      setSubmitState('success');
      setStatusMessage(text.status.success);
      return;
    }

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitState('error');
      setStatusMessage(text.status.fixErrors);
      return;
    }

    if (!webhookUrl) {
      setSubmitState('error');
      setStatusMessage(text.status.webhookMissing);
      return;
    }

    setSubmitState('submitting');
    setStatusMessage('');

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        website: form.website.trim(),
        objective: form.objective,
        message: form.message.trim(),
        consent: form.consent,
        language: lang,
        source: 'landing-contact-form',
        submittedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
        pageUrl: window.location.href,
      };

      const res = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!res.ok) {
        const responseBody = await res.text().catch(() => '');
        throw new Error(`Webhook error ${res.status}: ${responseBody}`);
      }

      setSubmitState('success');
      setStatusMessage(text.status.success);
      setForm({
        name: '',
        email: '',
        website: '',
        objective: 'more-leads',
        message: '',
        consent: false,
        honeypot: '',
      });
      setErrors({});
    } catch (error) {
      console.error('Contact form submit failed:', error);
      if (error instanceof Error && error.message.includes('410')) {
        setSubmitState('error');
        setStatusMessage(text.status.webhookInactive);
        return;
      }
      setSubmitState('error');
      setStatusMessage(text.status.errorGeneric);
    } finally {
      window.clearTimeout(timer);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-gradient-to-b from-slate-900 via-violet-900/20 to-slate-900">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div>
              <p className="text-violet-400 text-xs font-semibold tracking-[0.2em] uppercase mb-6">
                {text.label}
              </p>
              <h2 className="text-4xl md:text-[48px] font-light text-white mb-6 leading-tight tracking-[0.05em]">
                {text.headline1}
                <br />
                <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                  {text.headline2}
                </span>
              </h2>
              <p className="text-gray-400 text-base font-normal leading-[1.7] whitespace-pre-line">
                {text.description}
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/30 to-slate-700/30 rounded-3xl p-8 backdrop-blur-sm border border-gray-700/30 flex flex-col gap-6">
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
              <input
                type="text"
                name="honeypot"
                value={form.honeypot}
                onChange={(e) => updateField('honeypot', e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="sr-only"
                aria-hidden="true"
              />

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-name" className="text-sm text-white/80">
                  {text.fields.nameLabel}
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder={text.fields.namePlaceholder}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  className="w-full rounded-xl bg-slate-900/70 border border-gray-600/50 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.name ? <p id="name-error" className="text-xs text-red-300">{errors.name}</p> : null}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-email" className="text-sm text-white/80">
                  {text.fields.emailLabel}
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder={text.fields.emailPlaceholder}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className="w-full rounded-xl bg-slate-900/70 border border-gray-600/50 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.email ? <p id="email-error" className="text-xs text-red-300">{errors.email}</p> : null}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-website" className="text-sm text-white/80">
                  {text.fields.websiteLabel}
                </label>
                <input
                  id="contact-website"
                  name="website"
                  type="url"
                  value={form.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder={text.fields.websitePlaceholder}
                  aria-invalid={Boolean(errors.website)}
                  aria-describedby={errors.website ? 'website-error' : undefined}
                  className="w-full rounded-xl bg-slate-900/70 border border-gray-600/50 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.website ? <p id="website-error" className="text-xs text-red-300">{errors.website}</p> : null}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-objective" className="text-sm text-white/80">
                  {text.fields.objectiveLabel}
                </label>
                <select
                  id="contact-objective"
                  name="objective"
                  value={form.objective}
                  onChange={(e) => updateField('objective', e.target.value as Objective)}
                  aria-invalid={Boolean(errors.objective)}
                  aria-describedby={errors.objective ? 'objective-error' : undefined}
                  className="w-full rounded-xl bg-slate-900/70 border border-gray-600/50 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="more-leads">{text.fields.objectiveOptions.moreLeads}</option>
                  <option value="better-follow-up">{text.fields.objectiveOptions.betterFollowUp}</option>
                  <option value="more-bookings">{text.fields.objectiveOptions.moreBookings}</option>
                </select>
                {errors.objective ? <p id="objective-error" className="text-xs text-red-300">{errors.objective}</p> : null}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-message" className="text-sm text-white/80">
                  {text.fields.messageLabel}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => updateField('message', e.target.value)}
                  placeholder={text.fields.messagePlaceholder}
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                  className="w-full rounded-xl bg-slate-900/70 border border-gray-600/50 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.message ? <p id="message-error" className="text-xs text-red-300">{errors.message}</p> : null}
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="contact-consent"
                  name="consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => updateField('consent', e.target.checked)}
                  aria-invalid={Boolean(errors.consent)}
                  aria-describedby={errors.consent ? 'consent-error' : undefined}
                  className="mt-1 h-4 w-4 rounded border-gray-500 bg-slate-900 text-violet-500 focus:ring-violet-500"
                />
                <label htmlFor="contact-consent" className="text-xs text-white/70 leading-relaxed">
                  {text.fields.consentLabel}
                </label>
              </div>
              {errors.consent ? <p id="consent-error" className="text-xs text-red-300">{errors.consent}</p> : null}

              <button
                type="submit"
                disabled={submitState === 'submitting'}
                className="w-full block text-center bg-gradient-to-r from-violet-600 to-purple-600 text-white py-4 rounded-xl font-medium text-base hover:from-violet-500 hover:to-purple-500 transition-all duration-500 hover:scale-[1.01] hover:shadow-2xl hover:shadow-violet-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitState === 'submitting'
                  ? text.submitLoading
                  : submitState === 'success'
                    ? text.submitSuccess
                    : text.submitIdle}
              </button>

              <p role="status" aria-live="polite" className="text-sm text-white/80 min-h-5">
                {statusMessage}
              </p>
            </form>

            <a
              href="https://calendly.com/simplesflow/simplesflow"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center text-sm text-violet-300 hover:text-violet-200 transition-colors"
            >
              {text.fallback}
            </a>

            <div className="flex flex-col gap-3 mt-2">
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-violet-400 text-sm">
                  <i className="ri-map-pin-line" aria-hidden="true"></i>
                </span>
                <span className="text-white/50 text-[12px] font-light">{text.trust1}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-violet-400 text-sm">
                  <i className="ri-time-line" aria-hidden="true"></i>
                </span>
                <span className="text-white/50 text-[12px] font-light">{text.trust2}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
